import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon } from './Icon';
import { services } from '../data/services';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(1.875rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ServicesSection = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.theme.colors.white};
`;

const Container = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  .text-primary {
    color: ${props => props.theme.colors.text};
  }
  
  .blue-gradient {
    background: ${props => props.theme.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.75rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.1rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(21.875rem, 1fr));
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fit, minmax(18.75rem, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 0.0625rem solid ${props => props.theme.colors.lightGray};
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease-in-out;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-fill-mode: both;
  
  &:hover {
    transform: translateY(-0.3125rem);
    box-shadow: ${props => props.theme.shadows.lg};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const ServiceIconContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const ServiceIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  background: ${props => props.theme.gradients.primary};
  border-radius: 50%;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.white};
  
  svg {
    width: 2rem;
    height: 2rem;
  }
`;

const ServiceTitle = styled.h3`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const ServiceDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const ServiceButton = styled.button`
  border: 0.125rem solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  background: transparent;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0.625rem 1.25rem;
    font-size: 0.8rem;
  }
`;

export const Services: React.FC = () => {
  const handleServiceClick = (title: string) => {
    console.log(`Service clicked: ${title}`);
    // Placeholder for service interaction
  };

  return (
    <ServicesSection id="servicios">
      <Container>
        <SectionHeader>
          <SectionTitle>
            <span className="text-primary">Nuestros </span>
            <span className="blue-gradient">Servicios</span>
          </SectionTitle>
          <SectionDescription>
            Especialistas en vidrios y aluminios. Puertas, Ventanas, Cancelería de baños, Barandales, Espejos, Vidrios de diferentes espesor. Trabajos garantizados.
          </SectionDescription>
        </SectionHeader>

        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard 
              key={service.title}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <ServiceIconContainer>
                <ServiceIcon>
                  <Icon name={service.icon} size={32} />
                </ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceIconContainer>
              <ServiceButton onClick={() => handleServiceClick(service.title)}>
                Saber Más
              </ServiceButton>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};
